/**
 * @fileoverview Figure-impact panel for a generated report (MOR).
 *
 * After the practitioner edits a figure in the report by hand and saves, a
 * background scan flags every other figure that should reconcile with the
 * change ("if I change this figure, where does it flow through?").
 *
 * This surfaces inside the report assistant (chat) as a compact card: it lists
 * every flagged figure, each of which can be jumped to in the report, or handed
 * to the assistant to reconcile.
 */

import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Loader2,
  Sparkles,
  X,
} from "lucide-react";
import { Button } from "@/ui/primitives";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/ui/composed/overlays/alert-dialog";
import { cn } from "@/utils/misc";

export type FigureImpactSeverity = "high" | "medium" | "low";

export interface FigureImpactResult {
  sectionTitle: string | null;
  excerpt: string;
  reason: string;
  severity: FigureImpactSeverity;
  anchorText: string;
}

export interface FigureImpactScan {
  status: "scanning" | "ready" | "failed";
  changeSummary: string;
  results: FigureImpactResult[];
}

/**
 * Compact, reviewable list of figures affected by a hand edit, shown in the
 * report assistant.
 *
 * @component
 */
export function FigureImpactPanel({
  scan,
  onJumpTo,
  onReconcile,
  onReconcileAll,
  onDismiss,
}: {
  scan: FigureImpactScan;
  /** Scroll to + highlight the flagged figure in the report. */
  onJumpTo: (result: FigureImpactResult) => void;
  /** Hand the flagged figure to the assistant to reconcile. */
  onReconcile: (result: FigureImpactResult) => void;
  /** Hand every flagged figure to the assistant to reconcile in one pass. */
  onReconcileAll: () => void;
  onDismiss: () => void;
}) {
  // Dismissing a list of figures to review discards it, so confirm first.
  const [confirmDismiss, setConfirmDismiss] = useState(false);

  if (scan.status === "scanning") {
    return (
      <div className="mx-4 mb-2 flex items-center gap-2 rounded-xl border bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground">
        <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
        <span>
          Checking which figures flow through from your change
          {scan.changeSummary ? ` (${scan.changeSummary})` : ""}…
        </span>
      </div>
    );
  }

  if (scan.status === "failed") {
    return (
      <div className="mx-4 mb-2 flex items-center justify-between gap-2 rounded-xl border bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground">
        <span>Couldn&apos;t check for affected figures this time.</span>
        <DismissButton onDismiss={onDismiss} />
      </div>
    );
  }

  if (scan.results.length === 0) {
    return (
      <div className="mx-4 mb-2 flex items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300">
        <span>
          No other figures appear to depend on your change
          {scan.changeSummary ? ` (${scan.changeSummary})` : ""}.
        </span>
        <DismissButton onDismiss={onDismiss} subtle />
      </div>
    );
  }

  return (
    <div className="mx-4 mb-2 overflow-hidden rounded-xl border border-amber-200 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/40">
      <div className="flex items-start justify-between gap-2 border-b border-amber-200 px-3 py-2.5 dark:border-amber-900">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
          <div>
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
              {scan.results.length}{" "}
              {scan.results.length === 1 ? "figure" : "figures"} to review after
              your change
              {scan.changeSummary ? ` (${scan.changeSummary})` : ""}
            </p>
            <p className="text-xs text-amber-800/80 dark:text-amber-300/80">
              Figures may now be inconsistent and require review.
            </p>
          </div>
        </div>
        <DismissButton onDismiss={() => setConfirmDismiss(true)} />
        <AlertDialog open={confirmDismiss} onOpenChange={setConfirmDismiss}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Dismiss figures to review?</AlertDialogTitle>
              <AlertDialogDescription>
                This clears the{" "}
                {scan.results.length === 1
                  ? "figure"
                  : `${scan.results.length} figures`}{" "}
                flagged after your change. They won&apos;t be checked again
                unless you edit a figure and save, so make sure you&apos;ve
                reviewed any that still need reconciling.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep reviewing</AlertDialogCancel>
              <AlertDialogAction onClick={onDismiss}>Dismiss</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      {scan.results.length > 1 && (
        <div className="flex items-center justify-between gap-2 border-b border-amber-200/70 px-3 py-1.5 dark:border-amber-900/70">
          <span className="text-xs text-amber-800/80 dark:text-amber-300/80">
            Reconcile one at a time, or all together.
          </span>
          <Button
            size="sm"
            variant="default"
            className="h-7 px-2 text-xs"
            onClick={onReconcileAll}
          >
            <Sparkles className="mr-1 size-3.5" />
            Reconcile all
          </Button>
        </div>
      )}
      <ul className="divide-y divide-amber-200/70 dark:divide-amber-900/70">
        {scan.results.map((result, i) => {
          return (
            <li key={`${result.anchorText}:${i}`} className="px-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">
                  {result.excerpt}
                  {result.sectionTitle && (
                    <span className="ml-1.5 font-normal text-muted-foreground">
                      · {result.sectionTitle}
                    </span>
                  )}
                </p>
                {result.reason && <ImpactReason text={result.reason} />}
              </div>
              <div className="mt-1.5 flex items-center justify-end gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  onClick={() => onJumpTo(result)}
                >
                  <ArrowRight className="mr-1 size-3.5" />
                  Jump to
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  onClick={() => onReconcile(result)}
                >
                  <Sparkles className="mr-1 size-3.5" />
                  Reconcile
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The per-figure reason, clamped to two lines with a chevron to expand the
 * full text when it overflows (shown only when actually truncated).
 *
 * @component
 */
function ImpactReason({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  // Detect truncation while clamped (re-measured on container resize, e.g. the
  // assistant panel widening). Skip while expanded so the toggle stays shown.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      if (!expanded) setOverflowing(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, expanded]);

  return (
    <div className="flex items-start gap-1">
      <p
        ref={ref}
        className={cn(
          "min-w-0 flex-1 text-xs text-muted-foreground",
          !expanded && "line-clamp-2",
        )}
      >
        {text}
      </p>
      {(overflowing || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? "Show less" : "Show more"}
          className="mt-0.5 shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
        >
          {expanded ? (
            <ChevronUp className="size-3.5" />
          ) : (
            <ChevronDown className="size-3.5" />
          )}
        </button>
      )}
    </div>
  );
}

function DismissButton({
  onDismiss,
  subtle,
}: {
  onDismiss: () => void;
  subtle?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onDismiss}
      aria-label="Dismiss"
      className={cn(
        "shrink-0 rounded-md p-0.5 text-muted-foreground hover:text-foreground",
        subtle && "opacity-70",
      )}
    >
      <X className="size-4" />
    </button>
  );
}
